import { useCallback, useEffect, useState } from 'react';

import {
  applyColorScheme,
  resolveColorScheme,
  storeColorSchemePreference,
  type ColorScheme,
  type ColorSchemePreference,
} from './colorScheme';

/**
 * The reader's colour scheme, and a way to change it.
 *
 * All the machinery already existed — storage, resolution, following the OS —
 * with nothing wired to a control, so the choice was the operating system's
 * alone. This is the control.
 *
 * The applied scheme is read back off the document rather than kept in a second
 * piece of state: `initColorScheme` set it before React mounted, and a local
 * copy would be a second source of truth that starts out wrong.
 */
export interface ColorSchemeControl {
  readonly scheme: ColorScheme;
  readonly setPreference: (preference: ColorSchemePreference) => void;
}

function currentScheme(): ColorScheme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function useColorScheme(): ColorSchemeControl {
  const [scheme, setScheme] = useState<ColorScheme>(currentScheme);

  // The OS can still flip while no explicit choice is stored, and the attribute
  // changes underneath us when it does.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setScheme(currentScheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const setPreference = useCallback((preference: ColorSchemePreference) => {
    storeColorSchemePreference(preference);
    applyColorScheme(resolveColorScheme(preference));
  }, []);

  return { scheme, setPreference };
}
